import type { IAdminAuthRepository } from '@yawi/domain';
import type {
  AdminLoginResponse,
  AdminTokens,
  ImpersonateUserResponse,
  LoginDto,
  RefreshTokenDto,
  SelectTenantDto,
  TenantMembership,
} from '@yawi/types';
import { clearTokens, getApiClient, setTokens } from '../../http/apiClient';

// Prueba de login (5 min) que exige POST /auth/select-tenant.
let pendingSelectionToken: string | undefined;

export class ApiAdminAuthRepository implements IAdminAuthRepository {
  async login(dto: LoginDto): Promise<AdminLoginResponse> {
    const client = getApiClient();
    const response = await client.post<AdminLoginResponse>('v1/auth/login', dto);
    pendingSelectionToken = response.selectionToken;

    // Si hay tokens, guardarlos
    if (response.tokens) {
      await setTokens(response.tokens.accessToken, response.tokens.refreshToken, response.tokens.csrfToken);
    }

    return response;
  }

  async refreshTokens(dto: RefreshTokenDto): Promise<AdminTokens> {
    const client = getApiClient();
    const response = await client.post<AdminTokens>('/v1/auth/refresh', dto);

    await setTokens(response.accessToken, response.refreshToken, response.csrfToken);

    return response;
  }

  async logout(): Promise<void> {
    try {
      const client = getApiClient();
      await client.post('v1/auth/logout');
    } finally {
      await clearTokens();
    }
  }

  async selectTenant(dto: SelectTenantDto): Promise<AdminLoginResponse> {
    const client = getApiClient();
    const response = await client.post<AdminLoginResponse>('v1/auth/select-tenant', {
      ...dto,
      selectionToken: pendingSelectionToken,
    });
    pendingSelectionToken = undefined;

    // Si hay tokens, guardarlos
    if (response.tokens) {
      await setTokens(response.tokens.accessToken, response.tokens.refreshToken, response.tokens.csrfToken);
    }

    return response;
  }

  async getMyTenants(): Promise<TenantMembership[]> {
    const client = getApiClient();
    const response = await client.get<TenantMembership[]>('v1/auth/my-tenants');
    return response;
  }

  async accessTenant(tenantId: string): Promise<AdminLoginResponse> {
    const client = getApiClient();
    // Los tokens del tenant NO se guardan: reemplazaban la sesión del Super Admin
    // en el portal. Con x-client-type=mobile el backend los devuelve en el body
    // en vez de sobrescribir la cookie de refresh del portal.
    return client.post<AdminLoginResponse>(
      `v1/auth/admin/tenants/${tenantId}/access`,
      undefined,
      { headers: { 'x-client-type': 'mobile' } },
    );
  }

  async impersonateUser(tenantId: string, memberId: string): Promise<ImpersonateUserResponse> {
    const client = getApiClient();
    const response = await client.post<ImpersonateUserResponse>(
      `v1/auth/admin/tenants/${tenantId}/impersonate`,
      { memberId }
    );
    // Note: Tokens are NOT saved here - the caller (UI) decides what to do with them
    return response;
  }
}
